import {Component, OnInit, ViewChild} from '@angular/core';
import {IonSearchbar, NavParams} from '@ionic/angular';
import { Identity, TokenHelper } from '../../models/client';
import { RegisterDeviceByLocationDto } from '../../models/server/DataTransferObject/Objects/Devices';
import { LocationListDto } from '../../models/server/DataTransferObject/Objects/MasterData/Locations';
import { DeviceRegisterService } from '../../services/api/auth/device-register.service';
import { ModalService } from '../../services/ui/modal.service';
import { MeDto, TenantInfoDto } from '../../models/server/index-objects';
import { DeviceService } from '../../services/ui/device/device.service';
import { LoginService } from '../../services/api/auth/login.service';
import { PermissionService } from '../../services/api/permission/permission.service';
import { AppPermissions } from '../../models/server/Enums/Permissions';
import { ForbiddenError } from '../../models/client/errors/forbidden-error';

@Component({
  selector: 'app-device-register-manual',
  templateUrl: './device-register-manual.page.html',
  styleUrls: ['./device-register-manual.page.scss'],
})
export class DeviceRegisterManualPage implements OnInit {

  public locations: LocationListDto[] = [];
  public tenants: TenantInfoDto[] = [];
  public data: RegisterDeviceByLocationDto = new RegisterDeviceByLocationDto();
  public tenantId: number;
  public step: 'tenant' | 'location' | 'device' = 'location';
  public isLoading = false;

  private identity: Identity;
  private refreshToken: string;

  public currentSearch: string = null;

  public searchbar: IonSearchbar;
  @ViewChild('search', {static: false}) set searchbarViewChild(content: IonSearchbar) {
    this.searchbar = content;
  }

  constructor(
    private modalService: ModalService,
    private navParams: NavParams,
    private deviceRegisterService: DeviceRegisterService,
    private deviceService: DeviceService,
    private loginService: LoginService,
    private permissionService: PermissionService
  ) { }

  async ngOnInit() {
    this.identity = this.navParams.get('identity');
    this.refreshToken = this.navParams.get('refreshToken');
    const userInfo: MeDto = this.navParams.get('userInfo');

    if (this.refreshToken) {
      this.tenants = userInfo?.tenants || [];
      this.step = 'tenant';

      if (this.tenants.length === 1) {
        this.tenantId = this.tenants[0].tenantId;
        await this.next();
      }
    } else {
      await this.load();
    }

    this.data.deviceName = this.deviceService.getDeviceName();
  }

  clear() {
    this.data = new RegisterDeviceByLocationDto();
  }

  async exit() {
    this.clear();
    await this.modalService.dismissModal();
  }

  public async onSearch($event) {
    const value = $event.detail.value;
    this.currentSearch = value;

    await this.load(value);
  }

  async load(filter: string = null) {
    this.isLoading = true;

    try {
      this.locations = await this.deviceRegisterService.getLocations(this.identity, filter);
    } catch (error) {
      await (await this.modalService.createErrorAlertWithDetail(error)).present();
    } finally {
      this.isLoading = false;
    }
  }

  async next() {
    if (this.step === 'tenant') {
      if (!this.tenantId) {
        await (await this.modalService.createErrorAlert('LOGIN.NOTENANT')).present();
        return;
      }

      this.isLoading = true;
      try {
        const loginResponse = await this.loginService.loginRefresh(this.refreshToken, this.tenantId);
        this.identity = TokenHelper.getIdentityFromAppToken(loginResponse.body, false).identity;

        if (await this.permissionService.checkUserPermission(AppPermissions.UseDeviceRegister, this.identity.token) == false) {
          throw new ForbiddenError();
        }

        this.step = 'location';
        await this.load();
      } catch (error) {
        if (error instanceof ForbiddenError) {
          await (await this.modalService.createErrorAlert('DEVICEREGISTER.FORBIDDEN')).present();
        } else {
          await (await this.modalService.createErrorAlertWithDetail(error)).present();
        }
      } finally {
        this.isLoading = false;
      }
      return;
    }

    if (this.step === 'location') {
      if (!this.data.locationId) {
        await (await this.modalService.createErrorAlert('LOGIN.NOLOCATION')).present();
        return;
      }

      this.step = 'device';
      return;
    }

    this.data.deviceName = this.data.deviceName?.trim() ?? '';
    await this.modalService.dismissModal({ data: this.data, identity: this.identity });
  }
}
